import { Album, EnhancedFolder, EventType } from "../types";

export const typeMap: Record<EntityType, string> = {
  LibraryFolder: "f",
  LibraryFolderReference: "r",
  Gallery: "g",
  Album: "a"
};

export type EntityType =
  | "LibraryFolder"
  | "LibraryFolderReference"
  | "Gallery"
  | "Album";
interface AlbumType {
  id: number;
  type: "Album";
}

type PathSegment = string;

export function getEntitySegment(
  entity:
    | EnhancedFolder
    | EventType
    | Album
    | {
        id: number;
        referenceId: number | null;
        type: "Gallery" | "LibraryFolder" | "Album";
        isReference: boolean;
      }
): string {
  if (entity.type === "Album") {
    return `${typeMap.Album}:${entity.id}`;
  }

  const isReference =
    "isReference" in entity &&
    entity.isReference &&
    "referenceId" in entity &&
    entity.referenceId;
  const entityType = entity.type as EntityType;
  const prefix = isReference
    ? typeMap["LibraryFolderReference"]
    : typeMap[entityType];
  const id = isReference
    ? (entity as { referenceId: number }).referenceId
    : entity.id;

  return `${prefix}:${id}`;
}

function convertTrailToPath(trail: EnhancedFolder[]): PathSegment[] {
  return trail.map(getEntitySegment);
}

/**
 * Recursively search for target (folder or event) from rootFolders,
 * building the folder trail path along the way.
 */
export function findEntityPath(
  rootFolders: EnhancedFolder[],
  targetId: number,
  targetType: "folder" | "event" | "album",
  exactIdMatch: boolean = false
): string | null {
  const stack: { folder: EnhancedFolder; path: EnhancedFolder[] }[] = [];

  for (const root of rootFolders) {
    stack.push({ folder: root, path: [] });
  }

  while (stack.length > 0) {
    const { folder, path } = stack.pop()!;

    const currentTrail = [...path, folder];

    if (
      targetType === "folder" &&
      (folder.id === targetId ||
        (!exactIdMatch && folder.isReference && folder.referenceId === targetId))
    ) {
      return getEntityPathString(currentTrail);
    }

    if (targetType === "event" && Array.isArray(folder.events)) {
      const match = folder.events.find(e => {
        if (exactIdMatch) {
          return e.id === targetId && !e.isReference;
        }
        return e.id === targetId || (e.isReference && e.referenceId === targetId);
      });
      if (match) {
        const pathSegments = [
          ...convertTrailToPath(currentTrail),
          getEntitySegment(match)
        ];
        return getEntityPathString(pathSegments);
      }
    }

    if (Array.isArray(folder.children)) {
      for (const child of folder.children) {
        stack.push({ folder: child, path: currentTrail });
      }
    }
  }

  return null;
}

export function getEntityPathString(
  segments: EnhancedFolder[] | PathSegment[]
): string | null {
  if (!segments || segments.length === 0) return null;

  const pathSegments =
    typeof segments[0] === "string"
      ? (segments as PathSegment[])
      : convertTrailToPath(segments as EnhancedFolder[]);

  return pathSegments.join("/");
}
export function getEncodedEntityPath(path: string): string {
  return btoa(path); // encodes to Base64
}

//Decodes a base64 encoded entity path back to its original string
export function decodeEntityPath(encodedPath: string): string | null {
  try {
    return atob(encodedPath);
  } catch (error) {
    console.error("Failed to decode entity path:", error);
    return null;
  }
}

// First, let's create a reverse mapping of typeMap
const reverseTypeMap: Record<string, EntityType> = {
  f: "LibraryFolder",
  r: "LibraryFolderReference",
  g: "Gallery",
  a: "Album"
};

// Type for friendly entity type names
type FriendlyEntityType = "folder" | "reference" | "gallery" | "album";

const friendlyTypeMap: Record<EntityType, FriendlyEntityType> = {
  LibraryFolder: "folder",
  LibraryFolderReference: "reference",
  Gallery: "gallery",
  Album: "album"
};

/**
 * Gets the last segment from a path string
 */
function getLastSegment(path: string): PathSegment | null {
  const segments = path.split("/");
  return segments.length > 0 ? segments[segments.length - 1] : null;
}

/**
 * Extracts the entity ID from the last segment of a path
 */
export function getEntityIdFromPath(path: string): number | null {
  const lastSegment = getLastSegment(path);
  if (!lastSegment) return null;

  const parts = lastSegment.split(":");
  if (parts.length !== 2) return null;

  const id = parseInt(parts[1], 10);
  return isNaN(id) ? null : id;
}

/**
 * Extracts the entity type from the last segment of a path
 */
export function getEntityTypeFromPath(path: string): FriendlyEntityType | null {
  const lastSegment = getLastSegment(path);
  if (!lastSegment) return null;

  const prefix = lastSegment.split(":")[0];
  if (!prefix) return null;

  const entityType = reverseTypeMap[prefix];
  return entityType ? friendlyTypeMap[entityType] : null;
}

/**
 * Gets both type and ID from the last segment of a path
 */
export function getEntityInfoFromPath(path: string): {
  type: FriendlyEntityType | null;
  id: number | null;
} {
  return {
    type: getEntityTypeFromPath(path),
    id: getEntityIdFromPath(path)
  };
}

//Parses a decoded path string back into its individual segments
export function parseEntityPath(path: string): PathSegment[] {
  return path.split("/");
}

//Utility function that combines decoding and parsing

export function decodeAndParseEntityPath(
  encodedPath: string
): PathSegment[] | null {
  const decoded = decodeEntityPath(encodedPath);
  return decoded ? parseEntityPath(decoded) : null;
}

export function appendEntityToEncodedPath(
  encodedPath: string | null,
  entity: EnhancedFolder | EventType
): string {
  const decoded = encodedPath ? decodeEntityPath(encodedPath) : "";
  const segment = getEntitySegment(entity);
  const updatedPath = decoded ? `${decoded}/${segment}` : segment;
  return getEncodedEntityPath(updatedPath);
}

export function trimEncodedEntityPathTo(
  encodedPath: string,
  targetId: number,
  targetType: EntityType
): string | null {
  const decoded = decodeEntityPath(encodedPath);
  if (!decoded) return null;

  const targetPrefix = `${typeMap[targetType]}:${targetId}`;
  const segments = decoded.split("/");

  const index = segments.findIndex(segment => segment === targetPrefix);
  if (index === -1) {
    console.warn("Target entity not found in path");
    return null;
  }

  const trimmed = segments.slice(0, index + 1).join("/");
  return getEncodedEntityPath(trimmed);
}

/**
 * Extracts all entity IDs from the current path that should be highlighted/expanded
 */
export function getPathEntityIds(encodedPath: string | null): {
  folderIds: number[];
  eventIds: number[];
  albumIds: number[];
} {
  if (!encodedPath) {
    return { folderIds: [], eventIds: [], albumIds: [] };
  }

  const decoded = decodeEntityPath(encodedPath);
  if (!decoded) {
    return { folderIds: [], eventIds: [], albumIds: [] };
  }

  const segments = decoded.split("/");
  const folderIds: number[] = [];
  const eventIds: number[] = [];
  const albumIds: number[] = [];

  segments.forEach(segment => {
    const [prefix, idStr] = segment.split(":");
    const id = parseInt(idStr, 10);

    if (!isNaN(id)) {
      switch (prefix) {
        case "f": // LibraryFolder
        case "r": // LibraryFolderReference
          folderIds.push(id);
          break;
        case "g": // Gallery (Event)
          eventIds.push(id);
          break;
        case "a": // Album
          albumIds.push(id);
          break;
      }
    }
  });

  return { folderIds, eventIds, albumIds };
}

/**
 * Checks if a folder should be highlighted based on the current path
 * OLD LOGIC: highlights all folders in the path
 */
export function isFolderInPath(
  folderId: number,
  encodedPath: string | null
): boolean {
  const { folderIds } = getPathEntityIds(encodedPath);
  return folderIds.includes(folderId);
}

/**
 * Checks if an event should be highlighted based on the current path
 * OLD LOGIC: highlights all events in the path
 */
export function isEventInPath(
  eventId: number,
  encodedPath: string | null
): boolean {
  const { eventIds } = getPathEntityIds(encodedPath);
  return eventIds.includes(eventId);
}

/**
 * Checks if an album should be highlighted based on the current path
 * OLD LOGIC: highlights all albums in the path
 */
export function isAlbumInPath(
  albumId: number,
  encodedPath: string | null
): boolean {
  const { albumIds } = getPathEntityIds(encodedPath);
  return albumIds.includes(albumId);
}

/**
 * Helper function to check if the FINAL selected entity in the path matches the given entity
 * Handles both regular entities and reference entities
 */
function isTargetEntitySelected(
  entityId: number,
  encodedPath: string | null,
  expectedPrefix: string,
  isReference: boolean = false,
  referenceId?: number | null
): boolean {
  if (!encodedPath) return false;

  const decoded = decodeEntityPath(encodedPath);
  if (!decoded) return false;

  const segments = decoded.split("/");
  const lastSegment = segments[segments.length - 1];

  if (!lastSegment) return false;

  const [prefix, idStr] = lastSegment.split(":");
  const id = parseInt(idStr, 10);

  if (isNaN(id)) return false;

  // If this is a reference entity, check against the reference ID
  if (isReference && referenceId) {
    // Folder references use different prefixes (r: vs f:)
    // Event references use same prefix but different ID (g: with referenceId)
    const referencePrefix = expectedPrefix === "f" ? "r" : expectedPrefix;
    return prefix === referencePrefix && id === referenceId;
  }

  // For regular entities, check against the entity ID
  return prefix === expectedPrefix && id === entityId;
}

/**
 * Checks if a folder is the FINAL selected entity in the path
 */
export function isFolderSelected(
  folderId: number,
  encodedPath: string | null,
  isReference: boolean = false,
  referenceId?: number | null
): boolean {
  return isTargetEntitySelected(
    folderId,
    encodedPath,
    "f",
    isReference,
    referenceId
  );
}

/**
 * Checks if an event is the FINAL selected entity in the path
 */
export function isEventSelected(
  eventId: number,
  encodedPath: string | null,
  isReference: boolean = false,
  referenceId?: number | null
): boolean {
  return isTargetEntitySelected(
    eventId,
    encodedPath,
    isReference ? "r" : "g",
    isReference,
    referenceId
  );
}

/**
 * Checks if an album is the FINAL selected entity in the path
 */
export function isAlbumSelected(
  albumId: number,
  encodedPath: string | null
): boolean {
  return isTargetEntitySelected(albumId, encodedPath, "a");
}

/**
 * Utility function to get selection CSS class
 */
export function getSelectionClass(isSelected: boolean): string {
  return isSelected ? "selected" : "";
}

/**
 * Gets the IDs that should be auto-expanded based on the current path
 * For a path like f:1/f:2/g:3/a:4, we should expand:
 * - f:1 (to show f:2)
 * - f:2 (to show g:3)
 * - g:3 (to show a:4)
 *
 * The rule is: expand all entities that have children further down the path
 */
export function getAutoExpandIds(encodedPath: string | null): {
  folderIds: number[];
  eventIds: number[];
} {
  if (!encodedPath) {
    return { folderIds: [], eventIds: [] };
  }

  const decoded = decodeEntityPath(encodedPath);
  if (!decoded) {
    return { folderIds: [], eventIds: [] };
  }

  const segments = decoded.split("/");
  const folderIds: number[] = [];
  const eventIds: number[] = [];

  // Process each segment and check if it has children in the path
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const [prefix, idStr] = segment.split(":");
    const id = parseInt(idStr, 10);
    const isLastSegment = i === segments.length - 1;

    if (!isNaN(id)) {
      switch (prefix) {
        case "f": // LibraryFolder
        case "r": // LibraryFolderReference
          // Expand folders that have children in the path, OR if it's the final folder (to show its contents)
          if (!isLastSegment || isLastSegment) {
            folderIds.push(id);
          }
          break;
        case "g": // Gallery (Event)
          // Expand events that have albums in the path, OR if it's the final event (to show its albums)
          if (!isLastSegment || isLastSegment) {
            eventIds.push(id);
          }
          break;
        // Albums don't need expansion (they're leaf nodes)
      }
    }
  }

  return { folderIds, eventIds };
}

/**
 * Debug utility to log path expansion details
 */
export function logPathExpansion(encodedPath: string | null): void {
  if (!encodedPath) {
    return;
  }

  const decoded = decodeEntityPath(encodedPath);
  if (!decoded) {
    return;
  }

  const { folderIds, eventIds, albumIds } = getPathEntityIds(encodedPath);
  const { folderIds: expandFolders, eventIds: expandEvents } =
    getAutoExpandIds(encodedPath);
}
